// components/NewCaseWizard/VerticalStepper.tsx
import React from 'react';
import cn from 'classnames';
import { Check } from 'lucide-react'; // Or your preferred check icon

export interface Step {
  id: number;
  name: string;
  icon?: React.ReactNode;
}

interface VerticalStepperProps {
  steps: Step[];
  currentStep: number;
}

export const VerticalStepper = ({ steps, currentStep }: VerticalStepperProps) => {
  return (
    <nav aria-label="Progress" className="h-full">
      <ol role="list" className="flex h-full flex-col">
        {steps.map((step, stepIdx) => {
          const isCompleted = step.id < currentStep;
          const isCurrent = step.id === currentStep;
          const isUpcoming = step.id > currentStep;

          return (
            <li key={step.name} className="relative h-full">
              {/* Connector Line */}
              {stepIdx !== steps.length - 1 && (
                <div
                  className={cn(
                    'absolute left-4 w-0.5 -ml-px',
                    // Start the line from the bottom of the current icon.
                    'top-8',
                    // THE FIX: Extend the line to the middle of the NEXT icon.
                    // The next icon is h-8 (2rem high). Its middle is 1rem from its top.
                    // `bottom-[-1rem]` extends the line 1rem below its parent's boundary,
                    // perfectly centering it on the next icon.
                    'bottom-[-1rem]',
                    // Set color based on completion status.
                    isCompleted ? 'bg-teal-500' : 'bg-slate-200 dark:bg-gray-600'
                  )}
                  aria-hidden="true"
                />
              )}

              {/* Step Circle and Text */}
              <div className="flex items-center gap-4">
                <span
                  className={cn(
                    'relative flex h-8 w-8 items-center justify-center rounded-full',
                    isCompleted && 'bg-teal-500',
                    isCurrent && 'bg-blue-600',
                    isUpcoming && 'bg-slate-200 dark:bg-gray-600'
                  )}
                >
                  {isCompleted ? (
                    <Check className="h-5 w-5 text-white" />
                  ) : isCurrent && step.icon ? (
                    <span className="text-white">{step.icon}</span>
                  ) : isUpcoming && step.icon ? (
                    <span className="text-slate-500 dark:text-gray-400">{step.icon}</span>
                  ) : (
                    <span className={cn(isCurrent ? 'text-white' : 'text-slate-500 dark:text-gray-400')}>
                      {step.id}
                    </span>
                  )}
                </span>
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-slate-400 dark:text-gray-500">
                    STEP {step.id}
                  </span>
                  <span
                    className={cn(
                      'text-sm font-semibold',
                      isCurrent ? 'text-blue-600 dark:text-blue-400' : 'text-title-page-color-300 dark:text-gray-300'
                    )}
                  >
                    {step.name}
                  </span>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};